import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { GovNotFulfilledListSelectControl } from '@/module/GovEvaluation/form/controls/GovNotFulfilledListSelectControl';

import { Content, FileInputControl, Form, NumberInputControl } from '@/core/components';

interface GovEvaluationMinimalStandardFormProps {
  onSubmit: (v: GovEvalMinStdSchema) => void;
  isSubmitting: boolean;
  onClose: () => void;
}

const schema = z.object({
  notFulfilledList: z.array(z.object({ name: z.string() })),
  totalScore: z.number().min(0),
  proofFile: z.instanceof(File),
  editableProofFile: z.instanceof(File),
});

export type GovEvalMinStdSchema = z.infer<typeof schema>;

export function GovEvaluationMinimalStandardForm(props: GovEvaluationMinimalStandardFormProps) {
  const { onClose, isSubmitting, onSubmit } = props;
  const defaultValues = {
    notFulfilledList: [],
    totalScore: 0,
  };
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<GovEvalMinStdSchema>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      onCancel={onClose}
      title=""
      isSubmitting={isSubmitting}
    >
      <Group align="flex-start">
        <Content
          h="100%"
          type="full"
        >
          <Stack
            h="100%"
            gap="xl"
          >
            <NumberInputControl
              label={t('govEvaluation.totalScore')}
              name="totalScore"
              control={control}
              isSubmitting={isSubmitting}
              allowDecimal
              min={0}
            />
            <FileInputControl
              withAsterisk
              name="proofFile"
              control={control}
              label={t('govEvaluation.signedMinStd')}
              isSubmitting={isSubmitting}
              accept="application/pdf"
            />
            <FileInputControl
              withAsterisk
              name="editableProofFile"
              control={control}
              label={t('govEvaluation.editableMinStd')}
              isSubmitting={isSubmitting}
              accept="application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            />
          </Stack>
        </Content>
        <Content
          h="100%"
          type="full"
        >
          <GovNotFulfilledListSelectControl
            label={t('govEvaluation.notFulfilledList')}
            name="notFulfilledList"
            control={control}
            isSubmitting={isSubmitting}
          />
        </Content>
      </Group>
    </Form>
  );
}
